import { proxyNavigateTo as NavigateTo } from '@/utils/navigateTo';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { IrstoreTracker as IrTrack } from '@/utils/tracker';
import { debounce, isWxMiniProgram, getFileViewerUrl } from '@/utils/api';
import { resolveStoreId, resolvePageId } from '@rebuild/utils/context';
import {
	handleCoreToRoadshow,
	handleCoreToOffline,
	handleCoreToResearch,
	handleCoreToCompany
} from '@/utils/enterprise';
import { useStore } from '@/utils/vue-api';
import { getCurrentInstance } from 'vue';

const useNavigateStore = () => {
	const vm = getCurrentInstance();
	const store = useStore();
	const { goToModuleSubPath } = useNavigation();

	const navigateToNews = item => {
		if (item.url && item.content) {
			// 来源为微信公众号时, 直接跳转源链接
			const isWxSource = item.sourceType && item.sourceType == 3;
			if (isWxSource) {
				return NavigateTo({ path: item.url });
			}
			// 小程序访问->跳转IRM页面
			if (isWxMiniProgram) {
				return goToModuleSubPath('news', 'detail', {
					newsId: item.id
				});
			}
			// 否则，H5&APP跳转源链接
			return NavigateTo({ path: item.url });
		}

		// 2. 新闻仅包含源链接
		if (item.url) {
			return NavigateTo({ path: item.url });
		}

		// 3. 新闻仅包含富文本
		if (item.content) {
			return goToModuleSubPath('news', 'detail', {
				newsId: item.id
			});
		}
	};

	const navigateToRoadshow = item => {
		if (item && item.sourceType == 1) {
			//用户自主添加
			return NavigateTo({
				path: item.descUrl
			});
		}
		handleCoreToRoadshow({
			id: item.roadshowId,
			ct: item.status === '3' ? 1 : 0,
			openStatus: item.openstatus,
			descUrl: item.descUrl,
			sourceType: item.sourceType
		});
	};
	const navigateToOffline = item => {
		if (item && item.sourceType == 1) {
			//用户自主添加
			return NavigateTo({
				path: item.descUrl
			});
		}
		handleCoreToOffline(item);
	};
	const navigateToResearch = item => {
		handleCoreToResearch(item);
	};
	const navigateToActivity = item => {
		// businessTypes 3:调研纪要 8:路演活动 9:调研活动
		const { businessTypes } = item;
		if (typeof businessTypes === 'undefined') return;

		switch (businessTypes) {
			case 8:
				navigateToRoadshow(item);
				break;
			case 9:
				navigateToOffline(item);
				break;
			case 3:
				navigateToResearch(item);
				break;

			default:
				break;
		}
	};

	const navigateToReport = item => {
		if (!item.url) {
			return goToModuleSubPath('report', 'detail', {
				reportId: item.reportId,
				reportType: item.type
			});
		}
		const obj = {
			file: getFileViewerUrl(item.url, item.title),
			title: item.title,
			minPath: item.url
		};
		NavigateTo(obj);
	};

	const navigateToESG = item => {
		const file = item.url || item.fileUrl;
		if (!file) return;
		if (item.url) {
			NavigateTo({
				path: file
			})
			return;
		}
		const obj = {
			file: getFileViewerUrl(file, item.subject),
			title: item.subject,
			minPath: file
		};
		NavigateTo(obj);
	};

	const navigateToPerformanceReport = item => {
		const file = item.announcementLink;
		if (!file) return;

		NavigateTo({
			file: getFileViewerUrl(file, item.title || item.performanceName),
			title: item.title || item.performanceName,
			minPath: file
		});
	};

	const formComein = link => {
		if (!link) return false;
		return link.indexOf('.comein.cn') === -1;
	};
	const beforeAnnouncementNavigate = url => {
		if (!url) {
			store.dispatch('showDialog', {
				dialog_alert: true,
				dialog_text: '该文件地址失效，暂不支持打开',
				dialog_alert_data: [
					{
						btn: '知道了',
						callback: () => {
							store.dispatch('hideDialog');
						}
					}
				]
			});
			return;
		}

		const unValidLink = formComein(url);
		if (unValidLink && isWxMiniProgram) {
			vm.proxy.$copyText(url).then(
				e => {
					store.dispatch('showTooltip', {
						msg: '小程序禁止访问，已复制链接至粘贴板，可粘贴链接至浏览器查看',
						time: 2000
					});
				},
				e => {
					store.dispatch('showTooltip', {
						msg: '访问失败',
						time: 2000
					});
				}
			);
			return;
		}

		return true;
	};
	// 美股公告
	const navigateToUsAnnouncement = item => {
		const url = item.url;

		const state = beforeAnnouncementNavigate(url);
		if (!state) return;

		NavigateTo({ path: url });
	};
	const navigateToAnnouncement = item => {
		const url = item.comeinLink || item.sourceLink;
		const title = item.id + item.title;

		const state = beforeAnnouncementNavigate(url);
		if (!state) return;

		NavigateTo({
			file: getFileViewerUrl(url, title),
			title: title,
			minPath: url
		});
	};

	const beforeCustomNavigate = url => {
		if (!url) {
			return store.dispatch('showTooltip', {
				msg: '暂无活动文件'
			});
		}
		return true;
	};
	const navigateToCustom = item => {
		if (item.type == 2) {
			return NavigateTo({
				path: item.url
			});
		}

		const url = item.fileUrl;
		const title = item.fileName;

		const state = beforeCustomNavigate(url);
		if (!state) return;

		NavigateTo({
			file: getFileViewerUrl(url, title),
			minPath: url,
			title
		});
	};

	const navigateToDisplay = item => {
		let options;
		if (item.skipType === 1) {
			options = {
				file: getFileViewerUrl(item.skipUrl, item.name)
			};
		} else {
			options = {
				path: item.skipUrl
			};
		}
		NavigateTo(options);
	};

	const navigateToCompanyProfile = (params = {}) => {
		const { option, isOwn } = params;
		handleCoreToCompany(option, isOwn);
	};

	const navigateToBanner = item => {
		if (!item.url) return;
		if (item.type == 'VIDEO') return;
		if (item.type == 'DOCUMENT') {
			NavigateTo({
				file: getFileViewerUrl(item.url, item.title),
				title: item.title
			});
		} else {
			NavigateTo({
				path: item.url
			});
		}
	};

	const navigateToQuarterlyPerformanceRoadshow = item => {
		if (!item.roadshowLink) return;
		location.href = item.roadshowLink;
	};
	const navigateToQuarterlyPerformanceImagePreview = item => {
		if (!item.longImage) return;
		NavigateTo({
			name: 'enterprise_preview',
			query: {
				imageUrl: item.longImage
			}
		});
	};
	const navigateToQuarterlyPerformanceAnnouncement = item => {
		const url = item.announcementLink;
		const state = beforeAnnouncementNavigate(url);
		if (!state) return;

		const title = '业绩公告';
		NavigateTo({
			file: getFileViewerUrl(url, title),
			title: title,
			minPath: url
		});
	};
	const navigateToQuarterlyPerformanceSummary = item => {
		const url = item.roundupLink;
		const state = beforeAnnouncementNavigate(url);
		if (!state) return;

		const title = '业绩摘要';
		NavigateTo({
			file: getFileViewerUrl(url, title),
			title: title,
			minPath: url
		});
	};
	const navigateToQuarterlyPerformanceCustom = item => {
		const url = item.showLink;
		const state = beforeCustomNavigate(url);
		if (!state) return;

		const title = '演示材料';
		NavigateTo({
			file: getFileViewerUrl(url, title),
			minPath: url,
			title
		});
	};

	const navigateToCorporateGovernance = item => {
		const file = item.url || item.fileUrl;
		if (!file) return;
		if (item.url) {
			NavigateTo({
				path: file
			})
			return;
		}
		let obj = {
			file: getFileViewerUrl(file, item.subject),
			title: item.subject
		};
		NavigateTo(obj);
	};

	const storeNavigators = {
		news: navigateToNews,
		investment_activity: navigateToActivity,
		roadshow: navigateToRoadshow,
		offline: navigateToOffline,
		research: navigateToResearch,
		report: navigateToReport,
		esg: navigateToESG,
		performance_report: navigateToPerformanceReport,
		us_announcement: navigateToUsAnnouncement,
		announcement: navigateToAnnouncement,
		custom: navigateToCustom,
		display: navigateToDisplay,
		company_profile: navigateToCompanyProfile,
		banner: navigateToBanner,
		quarterly_performance_roadshow: navigateToQuarterlyPerformanceRoadshow,
		quarterly_performance_image_preview: navigateToQuarterlyPerformanceImagePreview,
		quarterly_performance_announcement: navigateToQuarterlyPerformanceAnnouncement,
		quarterly_performance_summary: navigateToQuarterlyPerformanceSummary,
		quarterly_performance_custom: navigateToQuarterlyPerformanceCustom,
		corporate_governance: navigateToCorporateGovernance
	};

	return {
		storeNavigators
	};
};

export function useModuleNavigation() {
	const { storeNavigators } = useNavigateStore();

	const moduleNavigate = (moduleId, ...rest) => {
		const navigator = storeNavigators[moduleId];
		if (!navigator) {
			console.warn(`未找到模块 ${moduleId} 的跳转处理函数`);
			return;
		}
		return navigator(...rest);
	};

	const viewModule = debounce((moduleId, ...rest) => {
		irTrackSend(moduleId, ...rest);
		return moduleNavigate(moduleId, ...rest);
	}, 300);

	return {
		viewModule
	};
}

const storeNames = {
	news: '新闻资讯',
	investment_activity: '投关活动',
	roadshow: '路演活动',
	offline: '调研活动',
	research: '调研纪要',
	report: '券商研报',
	esg: 'ESG',
	performance_report: '业绩报告',
	us_announcement: '美股公告',
	announcement: '公司公告',
	custom: null,
	display: '展示模块',
	company_profile: '企业介绍',
	banner: '运营卡片',
	quarterly_performance_roadshow: '季度业绩/业绩路演',
	quarterly_performance_image_preview: '季度业绩/一图读懂',
	quarterly_performance_announcement: '季度业绩/业绩公告',
	quarterly_performance_summary: '季度业绩/业绩摘要',
	quarterly_performance_custom: '季度业绩/演示材料',
	corporate_governance: '企业管治'
};
const irTrackSend = (moduleId, item, ...rest) => {
	const storeId = resolveStoreId();
	const pageId = resolvePageId();
	if (!storeId || !pageId) return;

	const { id, ...restP } = item || {};

	const obj = {
		curPage: '投资者关系专栏',
		content: '查看详情',
		event: 'CLICK',
		module: '投资者关系专栏',
		params: {
			'内容ID': id,
			'企业ID': storeId,
			'页面ID': pageId
		}
	};

	let contentTitle;
	if (moduleId === 'custom') {
		contentTitle = rest?.[0] || '自定义模块';
	} else {
		contentTitle = storeNames[moduleId];
	}
	obj.params['点击内容名称'] = contentTitle;

	let selfContent = '自定义添加内容';
	if (moduleId === 'roadshow' || moduleId === 'offline') {
		if (restP.sourceType !== 1) selfContent = '默认内容';
	}
	if (moduleId === 'investment_activity') {
		if (restP.businessTypes === 8 || restP.businessTypes === 9) {
			if (restP.sourceType !== 1) selfContent = '默认内容';
		}
	}
	obj.params['点击内容类型'] = selfContent;

	if (moduleId === 'display') {
		obj.params['模块名称'] = restP.name;
		obj.params['模块ID'] = restP.moduleId;
	}

	IrTrack.send(obj);
};